import { useCallback } from "react";

import useMarkMentionsSeen, {
  getUnseenMentionKey
} from "components/mentions/use-mark-mentions-seen.hook";
import { useMarkStaffTaskMentionsSeenMutation } from "services/staff-tasks";
import type { StaffTask } from "services/types/staff-tasks.type";

/**
 * "d:301,302|c:88" — what is still unseen for `userId` on this task: their unseen description
 * mentions and the comments whose mention of them has no `seen_at` yet. Empty when everything is
 * seen, so opening an already-read task sends nothing.
 */
export const getStaffTaskUnseenMentionKey = (
  task: StaffTask | undefined,
  userId: number | undefined
): string => {
  if (!task || !userId) return "";
  const description = getUnseenMentionKey(task.mentions, userId);
  const comments = (task.comments ?? [])
    .filter((comment) =>
      (comment.mentioned_users ?? []).some(
        (user) => user.id === userId && !user.seen_at
      )
    )
    .map((comment) => comment.id)
    .sort((a, b) => a - b)
    .join(",");
  if (!description && !comments) return "";
  return `d:${description}|c:${comments}`;
};

/**
 * When someone @mentioned on a task (description or comment) opens its view, record that they saw
 * it: the writer's "Seen" line, and the bell notification marked read. TEAM_MENTIONS_FEATURE.
 */
export default function useMarkStaffTaskMentionsSeen({
  enabled,
  task,
  currentUserId
}: {
  enabled: boolean;
  task: StaffTask | undefined;
  currentUserId: number | undefined;
}) {
  const [markSeen] = useMarkStaffTaskMentionsSeenMutation();
  const send = useCallback((id: number) => markSeen(id).unwrap(), [markSeen]);
  useMarkMentionsSeen({
    enabled,
    targetId: task?.id,
    unseenKey: getStaffTaskUnseenMentionKey(task, currentUserId),
    markSeen: send
  });
}
